/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useState } from "react"
import { IRecord } from "../../types/schema"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { Fields } from "@cd2/forms"

type IUser = IRecord<
  "skeem_users",
  | "name"
  | "skeem_email"
  | "phoneNumber"
  | "first_name"
  | "last_name"
  | "email"
  | "marketingAccepted"
>

export function UserSelectList({
  value,
  onChange,
  label,
  disabled,
  branchId,
  customerId,
  isClearable,
  filter,
  sort,
  placeholder,
  required,
  error,
}: {
  value?: string
  onChange(id: string, user?: IUser): void
  label?: string | null
  disabled?: boolean
  isClearable?: boolean
  branchId?: string
  customerId?: string
  filter?: any
  placeholder?: string
  sort?: { by: any; direction: "ASC" | "DESC" | "asc" | "desc" }
  required?: boolean
  error?: string
}) {
  const skeem = window.skeem

  const [selected, setSelected] = useState<IUser | null>(null)

  function handleChange(user?: IUser) {
    setSelected(user || null)
    onChange(user?.id ?? "", user)
  }

  async function loadSelected() {
    if (value && selected?.id !== value) {
      const record = await skeem.fetch2<IUser>({
        first: {
          name: "skeem_users",
          attributes: [
            "name",
            "skeem_email",
            "phoneNumber",
            "first_name",
            "last_name",
            "marketingAccepted",
          ],
          filter: { matches: { id: value } },
          sort,
        },
      })
      setSelected(record)
    }
  }

  useAsyncEffect(loadSelected, [value])

  async function loadUsers(query: string) {
    const filterData: any[] = [filter].filter(Boolean)
    if (branchId) {
      filterData.push({ matches: { branch: branchId } })
    }
    if (customerId) {
      filterData.push({ matches: { customer: customerId } })
    }
    if (query) {
      filterData.push({
        or: [{ matches: { name: { like: query } } }, { matches: { skeem_email: { like: query } } }],
      })
    }
    const records = await skeem.fetch2<IUser[]>({
      model: {
        name: "skeem_users",
        attributes: [
          "name",
          "skeem_email",
          "phoneNumber",
          "first_name",
          "last_name",
          "marketingAccepted",
        ],
        filter: filterData.length > 0 ? { and: filterData } : undefined,
        sort,
        paginate: { size: 20 },
      },
    })
    return records
  }

  const displayLabel = label === null ? null : label ?? "User"

  return (
    <Fields.AsyncSelect
      label={displayLabel}
      loadOptions={loadUsers}
      showRequiredStar={required}
      value={selected}
      onChange={opt => handleChange(opt)}
      disabled={disabled}
      isClearable={isClearable}
      placeholder={placeholder}
      getOptionLabel={opt => opt.name}
      getOptionValue={opt => opt.id}
      error={error}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length === 0 ? "Type to search..." : "No results"
      }
      components={{
        SingleValue: props => {
          const { innerProps, getStyles } = props
          const record = props.data

          return (
            <div
              {...innerProps}
              style={{
                ...getStyles("singleValue", props),
              }}
            >
              <div>{record?.name}</div>
              <div
                css={css`
                  color: #333;
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                {record?.skeem_email}
              </div>
            </div>
          )
        },
        Option: props => {
          const { innerProps, innerRef, getStyles } = props
          const record = props.data
          return (
            <div
              ref={innerRef}
              {...innerProps}
              style={{
                ...getStyles("option", props),
              }}
            >
              <div>{record?.name}</div>
              <div
                css={css`
                  color: #333;
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                {record?.skeem_email}
              </div>
            </div>
          )
        },
      }}
    />
  )
}

export function UserSelectListSimple({
  users,
  onChange,
  value,
  label,
  isLoading,
  disabled,
}: {
  users: IRecord<"skeem_users", "name" | "skeem_email">[]
  onChange: (user?: IRecord<"skeem_users", "name" | "skeem_email">) => void
  value?: IRecord<"skeem_users", "name" | "skeem_email"> | string
  label?: React.ReactNode | null
  isLoading?: boolean
  disabled?: boolean
}) {
  const userRecord = typeof value === "object" ? value : users.find(v => v.id === value)
  return (
    <Fields.Select2
      label={label}
      options={users?.map(record => ({
        value: record.id,
        label: record.name,
        ...record,
      }))}
      onChange={onChange}
      value={userRecord}
      filterOption={(opt, query) =>
        !query ||
        opt.data?.name?.toLowerCase().includes(query.toLowerCase()) ||
        opt.data?.skeem_email?.toLowerCase().includes(query.toLowerCase())
      }
      isLoading={isLoading}
      getOptionLabel={opt => opt.name}
      getOptionValue={opt => opt.id}
      disabled={disabled}
      components={{
        SingleValue: props => {
          const { innerProps, getStyles, data } = props

          return (
            <div
              {...innerProps}
              style={{
                ...getStyles("singleValue", props),
              }}
            >
              <div>{data?.name}</div>
              <div
                css={css`
                  color: #333;
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                {data?.skeem_email}
              </div>
            </div>
          )
        },
        Option: props => {
          const { innerProps, innerRef, getStyles, data } = props

          return (
            <div
              ref={innerRef}
              {...innerProps}
              style={{
                ...getStyles("option", props),
              }}
            >
              <div>{data?.name}</div>
              <div
                css={css`
                  color: #333;
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                {data?.skeem_email}
              </div>
            </div>
          )
        },
      }}
    />
  )
}
